import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import type {
  RawSchedule,
  ScheduleFormSlotProps,
} from "@/core/components/floating-chatbot/slots";
import { ScheduleForm } from "../components/schedule-form";
import { fromApiFrequency } from "../mappers/frequency";
import { useReportScheduleMutations } from "../hooks/use-report-schedule-mutations";
import { REPORT_SCHEDULES_QUERY_KEY } from "../hooks/use-report-schedules";
import { toPayload } from "../mappers/to-payload";
import { ScheduleFormValues } from "../schema/schedule-form-schema";
import { getReportSchedules } from "../services/report-schedule-service";
import {
  CHAT_TRACKING,
  changedFields,
  countRecipients,
  ScheduleTrackingProvider,
  toErrorType,
  useScheduleTracking,
} from "../tracking";
import { NotificationSchedule } from "../types";
import { ScheduleListSlot } from "./schedule-list-slot";

/**
 * Slot do block `schedule_form`: renderiza o mesmo `ScheduleForm` da sheet dentro do chat.
 *
 * O submit vai direto ao woba-backend pelas mutations já existentes (que invalidam
 * `["report-schedules"]` e emitem os toasts), sem passar pelo agente.
 */
interface ScheduleFormSlotInternalProps extends ScheduleFormSlotProps {
  /**
   * Edição aberta a partir da lista deste mesmo card. Aí o `onDone` devolve o usuário à
   * lista (que já mostra o item atualizado), então não há nada a renderizar no lugar.
   * Ausente = o block veio do agente e o form é o card inteiro: encerrá-lo apagaria a
   * resposta da conversa.
   */
  fromList?: boolean;
}

export function ScheduleFormSlot(props: ScheduleFormSlotInternalProps) {
  return (
    <ScheduleTrackingProvider value={CHAT_TRACKING}>
      <ScheduleFormSlotContent {...props} />
    </ScheduleTrackingProvider>
  );
}

function ScheduleFormSlotContent({
  schedule,
  onDone,
  fromList = false,
}: ScheduleFormSlotInternalProps) {
  const [translate] = useTranslation(["insights"]);
  const queryClient = useQueryClient();
  const { create, update } = useReportScheduleMutations();
  const tracking = useScheduleTracking();

  // Preenchido quando o gate barra a criação: mostra o agendamento que já existe.
  const [blockedBy, setBlockedBy] = useState<NotificationSchedule[] | null>(
    null,
  );
  // Preenchido após salvar (criar ou editar): mantém o resultado visível no lugar do form.
  const [savedSchedule, setSavedSchedule] =
    useState<NotificationSchedule | null>(null);
  const [isChecking, setIsChecking] = useState(false);

  const editing = (schedule ?? null) as NotificationSchedule | null;

  if (savedSchedule) {
    return (
      <ScheduleListSlot
        schedules={[savedSchedule] as unknown as RawSchedule[]}
      />
    );
  }

  if (blockedBy) {
    return (
      <div className="flex flex-col gap-3">
        <p className="text-sm text-darkgray-700">
          {translate("schedule-chat-already-exists")}
        </p>
        <ScheduleListSlot
          schedules={blockedBy as unknown as RawSchedule[]}
          preferGiven
        />
      </div>
    );
  }

  const handleSubmit = async (values: ScheduleFormValues) => {
    const payload = toPayload(values);

    if (editing) {
      const before = fromApiFrequency(editing);
      update.mutate(
        { ...payload, id: editing.id },
        {
          onSuccess: (updated) => {
            tracking.updated(
              editing.id,
              changedFields(
                {
                  ...editing,
                  frequency: before.frequency,
                  weekday: before.weekdays[0],
                },
                values,
              ),
            );
            // Vindo da lista, `onDone` devolve para ela (que já reflete a alteração).
            // Vindo do agente, o form é o card inteiro: encerrá-lo apagaria a resposta,
            // então mostramos o agendamento atualizado no lugar.
            if (!fromList && updated) {
              setSavedSchedule(updated as NotificationSchedule);
              return;
            }
            onDone();
          },
          onError: (error) =>
            tracking.saveFailed(toErrorType(error), values.toolKey, "edit"),
        },
      );
      return;
    }

    // Gate de "1 agendamento por empresa" — a regra é só de UI (o woba-backend aceita N).
    // O form pode ter ficado na conversa enquanto o usuário criava um agendamento pela tela
    // de Insights, então relemos a lista ignorando o `staleTime` de 60s do hook: confiar no
    // cache (ou no que o agente viu ao emitir o block) permitiria um segundo agendamento.
    setIsChecking(true);
    try {
      const fresh = await queryClient.fetchQuery({
        queryKey: [REPORT_SCHEDULES_QUERY_KEY],
        queryFn: ({ signal }) => getReportSchedules({}, signal),
        staleTime: 0,
      });

      if (fresh.length > 0) {
        // Bloqueia em vez de converter em edição: os valores deste form sobrescreveriam em
        // silêncio a configuração que o usuário acabou de fazer na sheet.
        setBlockedBy(fresh);
        return;
      }

      create.mutate(payload, {
        onSuccess: (created) => {
          tracking.saved({
            scheduleId: created?.id,
            toolKey: values.toolKey,
            frequency: values.frequency,
            weekday: values.weekdays[0],
            sendTime: values.time,
            timezone: values.timezone,
            channels: values.channels,
            recipientsCount: countRecipients(values),
          });
          // Mostra o agendamento recém-criado no lugar do form. Descartar o card aqui
          // (onDone) deixava só o texto de introdução do agente na conversa, sem
          // resultado visível — o toast some e o histórico fica sem registro do que
          // foi criado.
          if (created) setSavedSchedule(created as NotificationSchedule);
          else onDone();
        },
        onError: (error) =>
          tracking.saveFailed(toErrorType(error), values.toolKey, "new"),
      });
    } catch {
      // Falha ao reler: não cria (não há como garantir a regra) e mantém o form editável.
      // O toast de erro fica a cargo da mutation quando o usuário tentar de novo.
    } finally {
      setIsChecking(false);
    }
  };

  return (
    <ScheduleForm
      schedule={editing ?? undefined}
      isSubmitting={isChecking || create.isPending || update.isPending}
      onCancel={onDone}
      onSubmit={handleSubmit}
    />
  );
}
